// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../layouts/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaChartLine,
  FaPhoneAlt,
  FaSms,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaUserPlus,
  FaCalendarCheck,
  FaCalendarDay,
  FaPaperPlane,
  FaRoute
} from 'react-icons/fa';
import { 
  getFinancialSummary, 
  getRecentCalls,
  getRecentSms,
  getRecentLeads,
  getUpcomingJobs,
  getSingleDayJobs,
  getRecentEstimates,
  getRecentSentEstimates,
  getStaleEstimates
} from '../services/dashboardService';
import FinancialOverview from '../components/dashboard/FinancialOverview';
import EstimatesByStatus from '../components/dashboard/EstimatesByStatus';
import RecentCalls from '../components/dashboard/RecentCalls';
import RecentSms from '../components/dashboard/RecentSms';
import RecentLeads from '../components/dashboard/RecentLeads';
import UpcomingJobs from '../components/dashboard/UpcomingJobs';
import SingleDayJobs from '../components/dashboard/SingleDayJobs';
import RecentEstimates from '../components/dashboard/RecentEstimates';
import RecentSentEstimates from '../components/dashboard/RecentSentEstimates';
import UpcomingCalls from '../components/dashboard/UpcomingCalls';
import { getCallDashboard } from '../modules/call-scheduling/callScheduleService';
import DateRangeSelector from '../components/dashboard/DateRangeSelector';
import StaleEstimate from '../components/dashboard/StaleEstimates';
import WeatherForecast from '../components/weather/WeatherForecast';
import LoadingSpinner from '../components/shared/LoadingSpinner';

const DashboardContainer = styled.div`
  padding: ${props => props.theme.spacing(2)};
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(3)};
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const QuickActionsRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1.5)};
  margin-bottom: ${props => props.theme.spacing(3)};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const QuickAction = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(2)};
  background: ${props => props.bg || `linear-gradient(135deg, ${props.theme.colors.primary}, #e07d00)`};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px ${props => props.shadowColor || 'rgba(246, 150, 32, 0.3)'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.shadowColor || 'rgba(246, 150, 32, 0.4)'};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  span {
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.85;
    margin-left: auto;
    white-space: nowrap;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => props.theme.spacing(3)};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidthCard = styled.div`
  grid-column: 1 / -1;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-width: 0;

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing(2)};
  }
`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30days');
  const [customRange, setCustomRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  });
  
  const [financialData, setFinancialData] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentSms, setRecentSms] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [singleDayJobs, setSingleDayJobs] = useState([]);
  const [recentEstimates, setRecentEstimates] = useState([]);
  const [recentSentEstimates, setRecentSentEstimates] = useState([]);
  const [staleEstimates, setStaleEstimates] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);

  const [loading, setLoading] = useState({
    financial: true,
    calls: true,
    sms: true,
    leads: true,
    upcomingJobs: true,
    singleDayJobs: true,
    estimates: true,
    sentEstimates: true,
    staleEstimates: true,
    scheduledCalls: true
  });

  const [error, setError] = useState({
    financial: null,
    calls: null,
    sms: null,
    leads: null,
    upcomingJobs: null,
    singleDayJobs: null,
    estimates: null,
    sentEstimates: null,
    staleEstimates: null,
    scheduledCalls: null
  });

  // Load financial data based on selected period
  useEffect(() => {
    const loadFinancialData = async () => {
      setLoading(prev => ({ ...prev, financial: true }));
      try {
        let params = { period };
        
        if (period === 'custom') {
          params.customStart = customRange.startDate.toISOString().split('T')[0];
          params.customEnd = customRange.endDate.toISOString().split('T')[0];
        }
        
        const data = await getFinancialSummary(params);
        setFinancialData(data);console.log("Financial data: ",data);
        setError(prev => ({ ...prev, financial: null }));
      } catch (err) {
        console.error('Error loading financial data:', err);
        setError(prev => ({ ...prev, financial: 'Failed to load financial data' }));
      } finally {
        setLoading(prev => ({ ...prev, financial: false }));
      }
    };
    
    loadFinancialData();
  }, [period, customRange]);
  
  // Load all other dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Load recent calls
        setLoading(prev => ({ ...prev, calls: true }));
        const callsData = await getRecentCalls();
        setRecentCalls(callsData);
        setError(prev => ({ ...prev, calls: null }));
        setLoading(prev => ({ ...prev, calls: false }));
        
        // Load recent SMS
        setLoading(prev => ({ ...prev, sms: true }));
        const smsData = await getRecentSms();
        setRecentSms(smsData);
        setError(prev => ({ ...prev, sms: null }));
        setLoading(prev => ({ ...prev, sms: false }));
        
        // Load recent leads
        setLoading(prev => ({ ...prev, leads: true }));
        const leadsData = await getRecentLeads();
        setRecentLeads(leadsData);
        setError(prev => ({ ...prev, leads: null }));
        setLoading(prev => ({ ...prev, leads: false }));
        
        // Load upcoming jobs
        setLoading(prev => ({ ...prev, upcomingJobs: true }));
        const upcomingJobsData = await getUpcomingJobs();
        setUpcomingJobs(upcomingJobsData);
        setError(prev => ({ ...prev, upcomingJobs: null }));
        setLoading(prev => ({ ...prev, upcomingJobs: false }));
        
        // Load upcoming scheduled calls (call bookings widget)
        setLoading(prev => ({ ...prev, scheduledCalls: true }));
        try {
          const callDash = await getCallDashboard({ upcoming: 8 });
          setScheduledCalls(callDash.upcoming || []);
          setError(prev => ({ ...prev, scheduledCalls: null }));
        } catch (callErr) {
          setError(prev => ({ ...prev, scheduledCalls: 'Failed to load scheduled calls' }));
        } finally {
          setLoading(prev => ({ ...prev, scheduledCalls: false }));
        }

        // Load single day jobs
        setLoading(prev => ({ ...prev, singleDayJobs: true }));
        const singleDayJobsData = await getSingleDayJobs();
        setSingleDayJobs(singleDayJobsData);
        setError(prev => ({ ...prev, singleDayJobs: null }));
        setLoading(prev => ({ ...prev, singleDayJobs: false }));
        
        // Load recent estimates
        setLoading(prev => ({ ...prev, estimates: true }));
        const estimatesData = await getRecentEstimates();
        setRecentEstimates(estimatesData);
        setError(prev => ({ ...prev, estimates: null }));
        setLoading(prev => ({ ...prev, estimates: false }));
        
        // Load recent sent estimates
        setLoading(prev => ({ ...prev, sentEstimates: true }));
        const sentEstimatesData = await getRecentSentEstimates();
        setRecentSentEstimates(sentEstimatesData);
        setError(prev => ({ ...prev, sentEstimates: null }));
        setLoading(prev => ({ ...prev, sentEstimates: false }));

        // Load stale estimates
        setLoading(prev => ({ ...prev, staleEstimates: true}));
        const staleEstimatesData = await getStaleEstimates();
        setStaleEstimates(staleEstimatesData);
        setError(prev => ({ ...prev, staleEstimates: null }));
        setLoading(prev => ({ ...prev, staleEstimates: false }));
        
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(prev => ({ 
          ...prev, 
          general: 'Failed to load some dashboard data. Please try refreshing the page.'
        }));
      }
    };
    
    loadDashboardData();
    
    // Set up periodic refresh every 5 minutes
    const refreshInterval = setInterval(loadDashboardData, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };
  
  const handleCustomRangeChange = (startDate, endDate) => {
    setCustomRange({ startDate, endDate });
  };

  return (
    <DashboardLayout>
      <DashboardContainer>
        <DashboardHeader>
          <Title>Dashboard</Title>
          <DateRangeSelector 
            period={period} 
            customRange={customRange}
            onPeriodChange={handlePeriodChange}
            onCustomRangeChange={handleCustomRangeChange}
          />
        </DashboardHeader>

        <QuickActionsRow>
          <QuickAction onClick={() => navigate('/leads/route-planner')}>
            <FaRoute /> Plan Route
            <span>Optimize visits</span>
          </QuickAction>
          <QuickAction
            onClick={() => navigate('/leads/new')}
            bg="linear-gradient(135deg, #3d5afe, #2840cb)"
            shadowColor="rgba(61, 90, 254, 0.3)"
          >
            <FaUserPlus /> Add Lead
          </QuickAction>
          <QuickAction
            onClick={() => navigate('/estimates/new')}
            bg="linear-gradient(135deg, #27ae60, #1e8e4e)"
            shadowColor="rgba(39, 174, 96, 0.3)"
          >
            <FaFileInvoiceDollar /> New Quote
          </QuickAction>
        </QuickActionsRow>

        <DashboardGrid>
          {/* Weather Forecast */}
          <FullWidthCard>
            <WeatherForecast />
          </FullWidthCard>

          {/* Financial Overview Section */}
          <FullWidthCard>
            <FinancialOverview 
              data={financialData} 
              loading={loading.financial}
              error={error.financial}
            />
          </FullWidthCard>
          
          {/* Scheduled Calls — today's & tomorrow's bookings highlighted */}
          <UpcomingCalls
            data={scheduledCalls}
            loading={loading.scheduledCalls}
            error={error.scheduledCalls}
          />

          {/* Estimates by Status Chart */}
          <EstimatesByStatus
            data={financialData?.estimatesByStatus} 
            loading={loading.financial}
            error={error.financial}
          />
          
          {/* Recent Calls */}
          <RecentCalls 
            data={recentCalls} 
            loading={loading.calls}
            error={error.calls}
          />
          
          {/* Recent SMS */}
          <RecentSms 
            data={recentSms} 
            loading={loading.sms}
            error={error.sms}
          />
          
          {/* Recent Leads */}
          <RecentLeads 
            data={recentLeads} 
            loading={loading.leads}
            error={error.leads}
          />
          
          {/* Upcoming Jobs */}
          <UpcomingJobs 
            data={upcomingJobs} 
            loading={loading.upcomingJobs}
            error={error.upcomingJobs}
          />
          
          {/* Single Day Jobs */}
          <SingleDayJobs 
            data={singleDayJobs} 
            loading={loading.singleDayJobs}
            error={error.singleDayJobs}
          />
          
          {/* Recent Estimates */}
          <RecentEstimates 
            data={recentEstimates} 
            loading={loading.estimates}
            error={error.estimates}
          />
          
          {/* Recent Sent Estimates */}
          <RecentSentEstimates 
            data={recentSentEstimates} 
            loading={loading.sentEstimates}
            error={error.sentEstimates}
          />

          {/* Recent Sent Estimates */}
          <StaleEstimate
            data={staleEstimates} 
            loading={loading.staleEstimates}
            error={error.staleEstimates}
          />
        </DashboardGrid>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default Dashboard;